import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { defaultIfEmpty, map, switchMap } from 'rxjs/operators';
import { Store } from '@ngxs/store';

import { UserIdentityService } from '@auth/services/user-identity.service';
import { CurrentOrganizationService } from '@auth/services/current-organization.service';
import { UserProfileService } from '@auth/services/user-profile.service';
import { ChangeOrganisation } from '@auth/state/actions/organization/change-organisation.action';
import { Permission } from '@auth/models/permission.enum';
import { UserModel } from '@auth/models/user/user.model';
import { OrganisationMappingModel } from '@auth/models/user/organisation-mapping.model';
import { LocaleSettingsService } from '@core/services/locale-settings.service';
import { RoleType } from '@core/models/authentication/role-type.enum';
import { OrganizationService } from './organization.service';
import { OrganizationModel } from '../models/user/organization.model';
import { PermissionModel } from '../models/user/permission.model';
import { OktaUserModel } from '../models/user/okta-user.model';
import { UserIdentity } from '@app/core/models/authentication/user-identity';

@Injectable()
export class UserService {
  public currentCulture = 'en';
  public orgValue = 1;

  private _currentUser!: UserModel;

  constructor(
    private identity: UserIdentityService,
    private store: Store,
    private userProfile: UserProfileService,
    private organizationService: OrganizationService,
    private currentOrganizationService: CurrentOrganizationService,
    private localeSettingsService: LocaleSettingsService) { }

  get currentUser(): UserModel {
    return this._currentUser;
  }

  public fetchUserData(): Observable<UserModel> {
    return this.identity.userIdentity().pipe(
      switchMap(user => this.userProfile.getTecToken().pipe(
        switchMap(tecToken =>
          forkJoin({ newUserModel: this.userProfile.getNewUser(tecToken, user.userId),
            userOrgs: this.organizationService.getUserOrganizations(tecToken) }).pipe(
            map(({ newUserModel, userOrgs }) => {
              const oldUserModel = this.toUserModel(newUserModel, userOrgs, user); 

              this._currentUser = oldUserModel;

              this.localeSettingsService.culture = newUserModel.localization.culture;
              this.localeSettingsService.dateFormat = newUserModel.localization.dateFormat;
              this.localeSettingsService.timeFormat = newUserModel.localization.timeFormat;
              this.localeSettingsService.decimalSeparator = newUserModel.localization.decimalSeparator;

              this.setOrganization(oldUserModel);
              this.orgValue = 1;
              
              return oldUserModel;
            }),

            switchMap(oldUser => this.currentOrganizationService.setFeatureSetting().pipe(
              map(() => oldUser),
              defaultIfEmpty(oldUser)
            ))
          )
        )
      )
      )
    );
  }

  public isAdmin(): boolean {
    const currentMapping = this.currentUser.userOrganisationMappings.find(m =>
      m.tecComId === this.currentOrganizationService.getCurrentOrganization().tecComId);
    if (currentMapping) {
      return currentMapping.role.id === 2;
    }

    return false;
  }

  public isTecComAdmin(): boolean {
    return this.currentUser.roles.some(role => role.toLowerCase() === RoleType.Admin.toLowerCase());
  }

  public hasPermission(permission: Permission): boolean {
    const currentMapping = this.currentUser.userOrganisationMappings.find(m =>
      m.tecComId === this.currentOrganizationService.getCurrentOrganization().tecComId);
    if (currentMapping) {
      const perm = currentMapping.permissions.find(p =>
        p.permission === permission);
      if (perm) {
        return perm.isActive;
      }
    }

    return false;
  }

  private setOrganization(currentUser: UserModel): void {
    if (!currentUser.userOrganisationMappings || currentUser.userOrganisationMappings.length < 1) {
      return;
    }

    let currentOrgTecId = this.currentOrganizationService.getSelectedOrganizationTecId();

    if (!(currentOrgTecId &&
      currentUser.userOrganisationMappings.some(
        (mapping: OrganisationMappingModel) => mapping.tecComId === currentOrgTecId))) {
      currentOrgTecId = currentUser.userOrganisationMappings[0].tecComId;
    }

    const currentSalesOrg = currentUser.userOrganisationMappings
      .find(e => e.tecComId === currentOrgTecId) || currentUser.userOrganisationMappings[0];

    this.store.dispatch(new ChangeOrganisation(currentSalesOrg.tecComId));
  }

  private toUserModel(newUserModel: OktaUserModel, userOrgs: OrganizationModel[], user: UserIdentity): UserModel {
    return {
      createdFromEmail: '',
      tecOrderUserId: '',
      email: newUserModel.email,
      firstName: newUserModel.firstName,
      lastName: newUserModel.lastName,
      userUid: newUserModel.userUid,
      isFederatedUser: newUserModel.isFederatedUser,
      phone: newUserModel.phone,
      language: newUserModel.language,
      roles: user.roles,
      userOrganisationMappings: userOrgs.map(u => this.toUserOrganisationMappings(u))
    };
  }

  private toUserOrganisationMappings(orgModel: OrganizationModel): OrganisationMappingModel {
    return {
      val: this.orgValue++,
      category: orgModel.category,
      organisationName: orgModel.name,
      organisationRole: orgModel.role,
      tecComId: orgModel.tecComId,
      colour: orgModel.color,
      role: {
        id: orgModel.userRole,
        name: ''
      },
      permissions: orgModel.permissions.legacyPermissions.map(p => this.toPermissionModel(p))
    };
  } 

  private toPermissionModel(permission: Permission): PermissionModel {
    return {
      permission: permission,
      isActive: true
    };
  }
}
